/*
 * Module to handle configuration options of a report
 */

define([
  "jquery",
  "marionette",
  "messaging/messenger",
  "modules/reportSplit/reports/configArea/configAreaView",
  "backbone",
  "bootstrapDatePicker",
  "modules/reportSplit/reports/reportArea/dataModel",
  "modules/reportSplit/reports/reportModel",
  "modules/reportSplit/reports/configArea/configAreaModel",
  "modules/reportSplit/reports/singleReport/reportUtilities",
  "moment-timezone",
], function (
  $,
  Marionette,
  Messenger,
  ConfigAreaView,
  Backbone,
  Datepicker,
  DataModel,
  ReportModel,
  ConfigAreaModel,
  ReportUtilities,
  Moment,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "configAreaModule";
      this.region = options.region;
      this.configEntries = options.collection;
      this.report = options.report;
      this.pendingNewEntry = false; //true if there is a blank new entry in the collection
      this.map = []; //maps parameter names to their indices (so we can retrieve them later on)
      this._setUpVents();
    },
    show: function () {
      const topicReportGroupName = "reports.groups.topic";
      const topicStatusReportName = "reports.topicStatus.name";
      const topicResponseStatusReportName = "reports.topicResponseStatus.name";
      this.groups = Messenger.reqres.request("report:groups");
      this.groups.onSynced(function () {
        this.groups.each(function (group) {
          if (group.get("name") === topicReportGroupName) {
            const topicReportModels = group.get("reports");
            this.responseStatusReportModel = this._getReportModelByName(
              topicResponseStatusReportName,
              topicReportModels,
            );
            this.topicStatusReportModel = this._getReportModelByName(
              topicStatusReportName,
              topicReportModels,
            );
          }
        }, this);
      }, this);

      const model = new ConfigAreaModel.configArea();
      this.model = model;
      model.configure(this.configEntries, this.report);

      //If there is a topic, set up a reqres to get the intent ID from the intent name
      //Todo: Potentially move this to initializer.  We're moving this in steps - started in the view.
      Messenger.reqres.setHandler(
        "reports:intentID",
        function (intentName) {
          const topicLookup = new DataModel.topicLookup({ intentName: intentName });
          topicLookup.fetch();
          return topicLookup;
        },
        this,
      );

      this.layout = new ConfigAreaView.configurationView({ model: model });

      this.layout.on(
        "update:report:config",
        function (responseStatusReportChecked) {
          //update the subtitle
          if (this.report.options.subHeader) {
            let subHeader = $.t(this.report.options.subHeader);
            subHeader = ReportUtilities.figureOutText(subHeader, true).escapeHtml();
            Messenger.vent.trigger("show:report:subheader", subHeader);
          } else {
            Messenger.vent.trigger("show:report:subheader", "");
          }
          //set the report to not be from history; since the user just clicked
          //the "Apply" button it is indeed not from history.
          //This fixes RD-1197
          this.report.fromHistory = false;
          //Removed trusted sites set from history
          Messenger.vent.trigger("set:parameter", "selectedSites", []);

          if (this.report.href === ReportModel.hrefs.TOPIC_LISTING_BATCH) {
            this._setTopicListingCount();
          } else if (this.report.href.startsWith("/topicStatus/")) {
            this.layout.model.set("responseStatusReportSelected", responseStatusReportChecked);
            if (responseStatusReportChecked) {
              Messenger.vent.trigger("update:report", this.responseStatusReportModel);
              this.report = this.responseStatusReportModel;
            } else {
              Messenger.vent.trigger("update:report", this.report);
            }
          } else if (this.report.href.startsWith("/topicResponseStatus/")) {
            this.layout.model.set("responseStatusReportSelected", responseStatusReportChecked);
            if (responseStatusReportChecked) {
              Messenger.vent.trigger("update:report", this.report);
            } else {
              Messenger.vent.trigger("update:report", this.topicStatusReportModel);
              this.report = this.topicStatusReportModel;
            }
          } else {
            Messenger.vent.trigger("update:report", this.report);
          }
        },
        this,
      );

      this.layout.on(
        "get:categories",
        function (id) {
          this.handleGetCategories(id);
        },
        this,
      );

      this.layout.on(
        "format:datepicker",
        function () {
          //date-picker-ize the date pickers
          let datePickerFormat;
          let format;
          const lang = Messenger.reqres.request("get:interface:language");
          let momentFormat;
          if (model.get("limitToMonths")) {
            format = Messenger.reqres.request("get:datepicker:date:format");
            momentFormat = Messenger.reqres.request("get:moment:monthly:format");
            datePickerFormat = {
              dateFormat: format,
              changeMonth: true,
              changeYear: true,
              startView: 1,
              minViewMode: 1,
              autoclose: 1,
              language: lang,
            };
          } else {
            format = Messenger.reqres.request("get:datepicker:date:format");
            momentFormat = Messenger.reqres.request("get:moment:date:format");
            datePickerFormat = {
              dateFormat: format,
              changeMonth: true,
              changeYear: true,
              startView: 1,
              autoclose: 1,
              language: lang,
            };
          }
          console.log(datePickerFormat);
          this.updateStartAndEndDateFormats(momentFormat, datePickerFormat);
        },
        this,
      );

      this.showRegion(this.layout); //don't use loading here

      //re-render if the languages or categories get synced later
      model.get("touchpoints").once(
        "sync",
        function () {
          this.layout.render();
        },
        this,
      );
      if (model.get("standardResponsesType")) {
        model.get("standardResponsesType").onSynced(function () {
          this.layout.render();
        }, this);
      }
      model.get("categories").onSynced(function () {
        let value;
        let category;
        const categories = Messenger.reqres.request("kb:categories");
        if (!model.get("selectedCategory")) {
          //if nothing is set, default to the first category code/value
          if (categories.length > 0) {
            category = categories.models[0];
            this.layout.model.set("selectedCategory", category.get("categoryId"));
            const values = category.getValues();
            if (values.length > 0) {
              value = values.at(0);
              this.layout.model.set("selectedCategoryValue", value.get("categoryValueId"));
              this.handleGetCategories(value.get("categoryId"));
            }
          }
        } else {
          for (let i = 0; i < categories.length; i++) {
            category = categories.models[i];
            for (let j = 0; j < category.getValues().length; j++) {
              value = category.getValues().at(j);
              if (value.get("categoryValueId") === model.get("selectedCategory")) {
                //set the exact one
                this.layout.model.set("selectedCategory", value.get("categoryId"));
                this.layout.model.set("selectedCategoryValue", model.get("selectedCategory"));
                this.handleGetCategories(value.get("categoryId"));
                break;
              }
            }
          }
        }
        this.layout.render();
      }, this);
      model.get("languages").once(
        "sync",
        function () {
          this.layout.render();
        },
        this,
      );
      if (model.get("departments")) {
        model.get("departments").once(
          "sync",
          function () {
            this.layout.render();
          },
          this,
        );
      }
      if (model.get("users")) {
        model.get("users").once(
          "sync",
          function () {
            this.layout.render();
          },
          this,
        );
      }

      if (model.get("topic") !== "" && model.get("topic") !== null) {
        const topicName = Messenger.reqres.request("topic", model.get("topic"));
        topicName.onSynced(function () {
          this.layout.model.set("selectedTopicName", topicName.get("intentName"));
          this.layout.render();
        }, this);
      }

      if (model.get("selectedTask")) {
        const taskModel = Messenger.reqres.request("task", { taskId: model.get("selectedTask") });
        taskModel.onSynced(function () {
          this.layout.model.set("selectedTaskName", taskModel.get("taskName"));
          this.layout.render();
        }, this);
      }
    },
    _getReportModelByName: function (name, reports) {
      this.report_name = name;
      let reportToReturn = null;
      reports.each(function (report) {
        if (report.get("name") === this.report_name) {
          reportToReturn = report;
        }
      }, this);
      if (!reportToReturn) {
        return {};
      }
      return reportToReturn.attributes;
    },
    handleGetCategories: function (id) {
      const categories = Messenger.reqres.request("kb:categories");
      let matchingCategoryValues = [];
      let catVal = "";
      let catValDisplay = "";
      for (let i = 0; i < categories.size(); i++) {
        if (categories.at(i).get("categoryId") === Number(id)) {
          matchingCategoryValues = categories.at(i).getValues();
          break;
        }
      }
      //If the category contains values, set the first one by default
      if (matchingCategoryValues.length > 0) {
        catVal = matchingCategoryValues.at(0).get("categoryValueId");
        catValDisplay = matchingCategoryValues.at(0).get("value");
      }
      this.layout.model.set("catVal", catVal);
      this.layout.model.set("catValDisplay", catValDisplay);
      this.layout.setCategoryValues(matchingCategoryValues);
    },
    processReport: function () {
      if (this.report.useArgumentDefaults) {
        //Set it to auto display so it doesn't update the URL when it is using default params
        this.report.autoDisplay = true;

        if (this.report.href === ReportModel.hrefs.TOPIC_LISTING_BATCH) {
          this._setTopicListingCount();
        } else {
          Messenger.vent.trigger("update:report", this.report);
        }

        this.report.autoDisplay = false;
      }
    },
    _setTopicListingCount: function () {
      const selectedTouchpoints = this.layout.getTouchpoints();
      const selectedLocales = this.layout.getLanguages();
      const topicCount = Messenger.reqres.request(
        "topic:listing:count",
        selectedTouchpoints,
        selectedLocales,
      );
      topicCount.once(
        "sync",
        function () {
          this.layout.model.set("topicCount", topicCount.get("value"));
          Messenger.vent.trigger("update:report", this.report);
        },
        this,
      );
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "report:updateInterval",
        function (choice) {
          let datePickerFormat;
          let endDate;
          let format;
          let momentFormat;
          let startDate;
          const lang = Messenger.reqres.request("get:interface:language");
          if (choice === "Monthly") {
            //Get the new formats we'll use
            format = Messenger.reqres.request("get:datepicker:date:format");
            momentFormat = Messenger.reqres.request("get:moment:monthly:format");
            datePickerFormat = {
              dateFormat: format,
              startView: 1,
              minViewMode: 1,
              autoclose: 1,
              language: lang,
            };

            startDate = this.layout.getStartDateInMillis();
            endDate = this.layout.getEndDateInMillis();
            this.layout.model.setStartDate(Moment(startDate).startOf("month").valueOf());
            this.layout.model.setEndDate(Moment(endDate).endOf("month").valueOf());
            this.layout.model.set("limitToMonths", true);
            this.layout.model.set("limitToYears", false);

            this.updateStartAndEndDateFormats(momentFormat, datePickerFormat);
          } else if (choice === "Yearly") {
            //Get the new formats we'll use
            format = Messenger.reqres.request("get:datepicker:date:format");
            momentFormat = Messenger.reqres.request("get:moment:yearly:format");
            datePickerFormat = {
              dateFormat: format,
              startView: 2,
              minViewMode: 2,
              autoclose: 1,
              language: lang,
            };

            startDate = this.layout.getStartDateInMillis();
            endDate = this.layout.getEndDateInMillis();
            this.layout.model.setStartDate(Moment(startDate).startOf("year").valueOf());
            this.layout.model.setEndDate(Moment(endDate).endOf("year").valueOf());
            this.layout.model.set("limitToMonths", false);
            this.layout.model.set("limitToYears", true);

            this.updateStartAndEndDateFormats(momentFormat, datePickerFormat);
          } else if (choice === "Daily" || choice === "Weekly" || choice === "Hourly") {
            //Get the new formats we'll use
            format = Messenger.reqres.request("get:datepicker:date:format");
            momentFormat = Messenger.reqres.request("get:moment:date:format");
            datePickerFormat = {
              dateFormat: format,
              startView: 0,
              minViewMode: 0,
              autoclose: 1,
              language: lang,
            };

            this.layout.model.set("limitToMonths", false);
            this.layout.model.set("limitToYears", false);

            this.updateStartAndEndDateFormats(momentFormat, datePickerFormat);
          }
        },
        this,
      );

      Messenger.vent.on(
        "set:parameter",
        function (key, value) {
          this.layout.model.set(key, value);
        },
        this,
      );

      /**
       * Handlers for fetching parameters
       * These are only configured when the configuration area is visible.
       * What happens when there is both a configuration area visible and a drilldown that can have parameters of its
       * own? That's a good question, we haven't got to that yet.
       */
      Messenger.reqres.setHandler("param:{startDateDashHour}", function () {
        return Moment().subtract(1, "days").valueOf();
      });
      Messenger.reqres.setHandler("param:{endDateDashHour}", function () {
        return Moment().valueOf();
      });
      Messenger.reqres.setHandler("param:{startDateDashDay}", function () {
        return Moment().subtract(1, "weeks").valueOf();
      });
      Messenger.reqres.setHandler("param:{endDateDashDay}", function () {
        return Moment().valueOf();
      });
      Messenger.reqres.setHandler("param:{startDateDashWeek}", function () {
        return Moment().startOf("year").valueOf();
      });
      Messenger.reqres.setHandler("param:{endDateDashWeek}", function () {
        return Moment().valueOf();
      });
      Messenger.reqres.setHandler("param:{startDateDashMonth}", function () {
        return Moment().startOf("year").valueOf();
      });
      Messenger.reqres.setHandler("param:{endDateDashMonth}", function () {
        return Moment().valueOf();
      });
      Messenger.reqres.setHandler("param:{startDateDashYear}", function () {
        return Moment(0).valueOf();
      });
      Messenger.reqres.setHandler("param:{endDateDashYear}", function () {
        return Moment().valueOf();
      });
      Messenger.reqres.setHandler(
        "param:{startDateMonth}",
        function () {
          return Moment(this.layout.getStartDateInMillis()).month();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{endDateMonth}",
        function () {
          return Moment(this.layout.getEndDateInMillis()).month();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{startDateYear}",
        function () {
          return Moment(this.layout.getStartDateInMillis()).year();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{endDateYear}",
        function () {
          return Moment(this.layout.getEndDateInMillis()).year();
        },
        this,
      );
      Messenger.reqres.setHandler("param:{timeZone}", function () {
        return Moment.tz.guess();
      });
      Messenger.reqres.setHandler(
        "param:{startDate}",
        function () {
          return this.layout.getStartDateInMillis();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{endDate}",
        function () {
          return this.layout.getEndDateInMillis();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{startDateFirstOfYear}",
        function () {
          return this.layout.getStartDateInMillis();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{startDateMonthly}",
        function () {
          //In case this needs separate handling in the future
          return this.layout.getStartDateInMillis();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{endDateMonthly}",
        function () {
          return this.layout.getEndDateInMillis();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{startDateMonth}",
        function () {
          return Moment(this.layout.getStartDateInMillis()).month();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{startDateYear}",
        function () {
          return Moment(this.layout.getStartDateInMillis()).year();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{endDateMonth}",
        function () {
          return Moment(this.layout.getEndDateInMillis()).month();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{endDateYear}",
        function () {
          return Moment(this.layout.getEndDateInMillis()).year();
        },
        this,
      );
      Messenger.reqres.setHandler("param:{snippetId}", () => this.layout.getSnippetId());
      Messenger.reqres.setHandler(
        "param:{topic}",
        function () {
          return this.layout.getTopic();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{touchpoint}",
        function () {
          return this.layout.getTouchpoints();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{agentTransition}",
        function () {
          return this.layout.getStandardResponse();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{abbreviated}",
        function () {
          return this.layout.getAbbreviated();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{language}",
        function () {
          return this.layout.getLanguages();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{languages}",
        function () {
          return this.layout.getLanguages();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{catVal}",
        function () {
          return this.layout.getCategory();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{interval}",
        function () {
          return this.layout.getInterval();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{escalation}",
        function () {
          return this.layout.getEscalation();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{agent}",
        function () {
          return this.layout.getAgents();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{matchType}",
        function () {
          return this.layout.getMatchType();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{statusId}",
        function () {
          return this.layout.getStatus();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{includeFinished}",
        function () {
          return this.layout.getIncludeFinished();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{reasonWithOrWithout}",
        function () {
          return this.layout.getReasonWithOrWithout();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{bucket}",
        function () {
          return this.layout.getBucket();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{surveyChoice}",
        function () {
          return this.layout.getSurveyChoice();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{offset}",
        function () {
          return 0; //just a placeholder, the dataModel will override this
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{batchSize}",
        function () {
          return 1; //will also be overridden by the dataModel
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{documentId}",
        function () {
          return this.layout.getDocumentId();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{documentName}",
        function () {
          return this.layout.getDocumentName();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{showBlankResponses}",
        function () {
          return this.layout.getShowBlankResponses();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{topicStatuses}",
        function () {
          return this.layout.getTopicStatuses();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{topicScheduledStatuses}",
        function () {
          return this.layout.getTopicScheduledStatuses();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{departments}",
        function () {
          return this.layout.getDepartments();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{taskChangeTypes}",
        function () {
          return this.layout.getTaskChangeTypes();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{taskOwners}",
        function () {
          return this.layout.getTaskOwners();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{taskPriorities}",
        function () {
          return this.layout.getTaskPriorities();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{topicEditStatuses}",
        function () {
          return this.layout.getTopicEditStatuses();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{users}",
        function () {
          return this.layout.getUsers();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{task}",
        function () {
          return this.layout.getTask();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{analyticsTags}",
        function () {
          return this.layout.getAnalyticsTags();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "param:{archivedTopics}",
        function () {
          return this.layout.getArchivedTopicOption();
        },
        this,
      );

      //Display param handlers
      Messenger.reqres.setHandler(
        "paramDisplay:{catVal}",
        function () {
          return this.layout.getCategoryDisplay();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{startDate}",
        function () {
          return this.layout.getStartDateFormatted();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{startDateMonthly}",
        function () {
          return Messenger.reqres.request(
            "get:formatted:monthly",
            this.layout.getStartDateInMillis(),
          );
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{endDate}",
        function () {
          return this.layout.getEndDateFormatted();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{endDateMonthly}",
        function () {
          return Messenger.reqres.request(
            "get:formatted:monthly",
            this.layout.getEndDateInMillis(),
          );
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{abbreviated}",
        function () {
          return this.layout.getAbbreviated();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{includeFinished}",
        function () {
          return this.layout.getIncludeFinished();
        },
        this,
      );
      Messenger.reqres.setHandler("paramDisplay:{snippetId}", () => this.layout.getSnippetName());
      Messenger.reqres.setHandler(
        "paramDisplay:{topic}",
        function () {
          return this.layout.getTopicName();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{reasonWithOrWithout}",
        function () {
          return this.layout.getReasonWithOrWithoutDisplay();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{surveyChoice}",
        function () {
          return this.layout.getSurveyChoiceDisplay();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{escalation}",
        function () {
          return this.layout.getEscalation();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{documentId}",
        function () {
          return this.layout.getDocumentId();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{documentName}",
        function () {
          return this.layout.getDocumentName();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{showBlankResponses}",
        function () {
          return this.layout.getShowBlankResponses();
        },
        this,
      );
      Messenger.reqres.setHandler(
        "paramDisplay:{task}",
        function () {
          return this.layout.getTaskName();
        },
        this,
      );

      Messenger.reqres.setHandler(
        "fetch:topic:listing:count",
        function () {
          return this.layout.model.get("topicCount");
        },
        this,
      );
    },
    //Tells the view to update start/end dates.  Reduces code repetition.
    updateStartAndEndDateFormats: function (momentFormat, datePickerFormat) {
      const time = this.layout.getStartDateInMillis();
      const displayFormat = Moment(time).format(momentFormat);
      //get rid of the old datepickers and specify a new one
      this.layout.updateStartDatePicker(displayFormat, datePickerFormat);

      //do the same for the end date
      const timeEnd = this.layout.getEndDateInMillis();
      const endDisplayFormat = Moment(timeEnd).format(momentFormat);
      //get rid of the old datepickers and specify a new one
      this.layout.updateEndDatePicker(endDisplayFormat, datePickerFormat);
    },
  });
});
